import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Platform, Dimensions } from 'react-native';
import { db, auth } from '../firebaseConfig';
import { collection, addDoc, getDocs, query, orderBy, limit, where, serverTimestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { queryWithCacheFallback, queryCachedSGPs } from '../services/cacheService';
import Constants from 'expo-constants';

const BackgroundQueryContext = createContext();

// Maximum concurrent queries per user
const MAX_CONCURRENT_QUERIES = 3;

// Screen size detection
const { width: SCREEN_WIDTH } = Dimensions.get('window');
const IS_MOBILE = SCREEN_WIDTH < 768;

// Query statuses
export const QueryStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
  CANCELLED: 'cancelled'
};

export function BackgroundQueryProvider({ children }) {
  // Active background queries (up to 3)
  const [activeQueries, setActiveQueries] = useState([]);
  
  // Query history (loaded from Firestore)
  const [queryHistory, setQueryHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Minimized state for the modal
  const [isMinimized, setIsMinimized] = useState(IS_MOBILE); // Auto-minimize on mobile
  
  // Position state: 'bottom-right' | 'bottom-left'
  const [modalPosition, setModalPosition] = useState('bottom-right');
  
  // Mobile-specific: completely hidden vs showing indicator
  const [isMobileHidden, setIsMobileHidden] = useState(false);
  
  // Track which completed queries have been viewed
  const [viewedCompletedQueries, setViewedCompletedQueries] = useState(new Set());
  
  // Notification sound ref
  const notificationSoundRef = useRef(null);
  
  // Track foreground query that can be converted to background
  const [foregroundQuery, setForegroundQuery] = useState(null);

  // Generate unique query ID
  const generateQueryId = () => `query_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Load query history from Firestore
  const loadQueryHistory = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.uid) {
      setHistoryLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'users', user.uid, 'query_history'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || new Date()
      }));
      setQueryHistory(history);
    } catch (error) {
      console.error('[BackgroundQuery] Error loading history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Load history on mount and auth changes
  useEffect(() => {
    loadQueryHistory();
  }, [loadQueryHistory]);

  // Save query to history
  const saveToHistory = async (queryData) => {
    const user = auth.currentUser;
    if (!user?.uid) return;

    try {
      const historyEntry = {
        queryId: queryData.id,
        prompt: queryData.prompt,
        params: queryData.params,
        status: queryData.status,
        resultsCount: queryData.results?.length || 0,
        sgpCount: queryData.sgpCount || 0,
        durationMs: queryData.durationMs || 0,
        createdAt: serverTimestamp(),
        completedAt: queryData.status === QueryStatus.COMPLETED ? serverTimestamp() : null,
        cached: queryData.cached || false,
        error: queryData.error || null
      };

      await addDoc(collection(db, 'users', user.uid, 'query_history'), historyEntry);
      
      // Reload history
      await loadQueryHistory();
    } catch (error) {
      console.error('[BackgroundQuery] Error saving to history:', error);
    }
  };

  // Add a new background query
  const addBackgroundQuery = useCallback(async (prompt, params = {}, options = {}) => {
    // Check for duplicate queries (same prompt already processing)
    const normalizedPrompt = prompt?.trim()?.toLowerCase();
    const existingQuery = activeQueries.find(q => 
      q.prompt?.trim()?.toLowerCase() === normalizedPrompt &&
      (q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING)
    );
    
    if (existingQuery) {
      console.warn('[BackgroundQuery] Duplicate query detected, skipping:', prompt);
      return { 
        success: true, 
        queryId: existingQuery.id,
        duplicate: true 
      };
    }
    
    // Check if we've reached max concurrent queries
    const processingQueries = activeQueries.filter(
      q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
    );
    
    if (processingQueries.length >= MAX_CONCURRENT_QUERIES) {
      console.warn('[BackgroundQuery] Max concurrent queries reached');
      return { 
        success: false, 
        error: 'Maximum concurrent queries (3) reached. Please wait for a query to complete.' 
      };
    }

    const queryId = generateQueryId();
    const startTime = Date.now();

    // Create new query object
    const newQuery = {
      id: queryId,
      prompt,
      params,
      status: QueryStatus.PENDING,
      progress: 0,
      progressMessage: 'Initializing...',
      results: null,
      error: null,
      startTime,
      cached: false,
      sgpCount: 0,
      durationMs: 0
    };

    // Add to active queries
    setActiveQueries(prev => [...prev, newQuery]);

    // Start processing asynchronously
    processQuery(queryId, prompt, params, options);

    return { success: true, queryId };
  }, [activeQueries]);

  // Process a query
  const processQuery = async (queryId, prompt, params, options = {}) => {
    const startTime = Date.now();
    
    // Update status to processing
    updateQueryState(queryId, {
      status: QueryStatus.PROCESSING,
      progress: 10,
      progressMessage: 'Checking cache...'
    });

    try {
      // Determine functions URL
      const isLocalhost = Platform.OS === 'web' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      const base = isLocalhost
        ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
        : (
            (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
            || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
            || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
            || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
            || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net'
          );

      // Check SGP cache first if applicable
      let sgpCacheResult = null;
      const betType = params.prefer || 'straight';
      
      // ⭐ Detect SGP intent from query text (not just params)
      // Note: \bsgps?\b matches both "sgp" and "sgps"
      const sgpIndicators = /\bsgps?\b|same\s*game\s*parlay|parlay|same\s*game/i;
      const hasSgpIntent = sgpIndicators.test(prompt) || betType === 'sgp' || betType === 'sgpx';
      
      console.log('[BackgroundQuery] SGP intent check:', { 
        prompt, 
        hasSgpIntent, 
        betType,
        matchResult: sgpIndicators.test(prompt)
      });
      
      if (hasSgpIntent) {
        updateQueryState(queryId, {
          progress: 20,
          progressMessage: 'Checking SGP cache...'
        });

        // ⭐ Extract team hint from query if not in params
        let teamHint = params.team;
        if (!teamHint) {
          // Remove SGP keywords and try to find team name
          // Note: Include "sgps" (plural) in the removal
          const cleanedQuery = prompt.toLowerCase()
            .replace(/\bsgps?\b|same\s*game\s*parlays?|parlays?|same\s*game|tonight|today|tomorrow/gi, '')
            .trim();
          // Use the remaining words as team hint
          if (cleanedQuery.length > 2) {
            teamHint = cleanedQuery;
          }
        }
        
        console.log('[BackgroundQuery] Team hint extracted:', { teamHint, originalPrompt: prompt });

        try {
          console.log('[BackgroundQuery] Checking SGP cache for:', { teamHint, league: params.league, prompt });
          sgpCacheResult = await queryCachedSGPs({
            league: params.league,
            sport: params.sport,
            team: teamHint,
            limit: 100, // Get more results
            max_age_hours: 24, // Longer cache validity
            sort_by: 'ev'
          });
          
          console.log('[BackgroundQuery] SGP cache result:', {
            success: sgpCacheResult?.success,
            sgpsCount: sgpCacheResult?.sgps?.length || 0,
            fixturesCount: sgpCacheResult?.fixtures?.length || 0
          });

          if (sgpCacheResult?.success && sgpCacheResult?.sgps?.length > 0) {
            // SGP cache hit!
            const durationMs = Date.now() - startTime;
            updateQueryState(queryId, {
              status: QueryStatus.COMPLETED,
              progress: 100,
              progressMessage: `Found ${sgpCacheResult.sgps.length} cached SGPs`,
              results: sgpCacheResult.sgps,
              cached: true,
              sgpCount: sgpCacheResult.sgps.length,
              durationMs
            });

            // Save to history
            const finalQuery = activeQueries.find(q => q.id === queryId) || { id: queryId, prompt, params };
            await saveToHistory({
              ...finalQuery,
              status: QueryStatus.COMPLETED,
              results: sgpCacheResult.sgps,
              cached: true,
              sgpCount: sgpCacheResult.sgps.length,
              durationMs
            });

            // Notify completion
            notifyQueryComplete(queryId, true);
            return;
          }
        } catch (sgpError) {
          console.warn('[BackgroundQuery] SGP cache error:', sgpError);
        }
      }

      // Use cache-first approach for main query
      updateQueryState(queryId, {
        progress: 40,
        progressMessage: 'Querying odds data...'
      });

      const queryResult = await queryWithCacheFallback(
        prompt,
        {
          ...params,
          defaults: {
            ...params.defaults,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            now: new Date().toISOString()
          }
        },
        (status) => {
          // Update progress based on cache status
          if (status.stage === 'cache_hit') {
            updateQueryState(queryId, {
              progress: 70,
              progressMessage: 'Processing cached results...'
            });
          } else if (status.stage === 'cache_miss') {
            updateQueryState(queryId, {
              progress: 50,
              progressMessage: 'Fetching live data...'
            });
          }
        }
      );

      updateQueryState(queryId, {
        progress: 80,
        progressMessage: 'Processing results...'
      });

      if (!queryResult.success) {
        throw new Error(queryResult.error || 'Query failed');
      }

      // Parse results
      let bodyJson = queryResult;
      if (queryResult.odds) {
        bodyJson = {
          results: [{
            items: queryResult.odds
          }]
        };
      }

      const arr = Array.isArray(bodyJson?.results) ? bodyJson.results : [];
      const items = arr.flatMap(r => Array.isArray(r?.items) ? r.items : [])
        .flatMap(x => Array.isArray(x?.odds) ? x.odds : [x]);

      // ⭐ Detect if this is an SGP response - check multiple indicators
      const resultLevelIsSGP = arr.some(r => r?.type === 'sgp'); // Result-level type
      const itemLevelIsSGP = items.some(o => o?.type === 'sgp' || Array.isArray(o?.legs));
      const metaSource = bodyJson?.meta?.source || queryResult?.meta?.source;
      const isCachedSGP = metaSource === 'sgp_cache';
      const queryMentionsSGP = /\bsgp\b|parlay|same\s*game/i.test(prompt);
      
      const isSGPResponse = resultLevelIsSGP || itemLevelIsSGP || isCachedSGP || (queryMentionsSGP && items.length > 0 && items[0]?.legs);
      
      console.log('[BackgroundQuery] Response analysis:', {
        resultCount: arr.length,
        itemCount: items.length,
        resultLevelIsSGP,
        itemLevelIsSGP,
        isCachedSGP,
        metaSource,
        queryMentionsSGP,
        isSGPResponse,
        firstItemKeys: items[0] ? Object.keys(items[0]).slice(0, 10) : []
      });

      let transformedResults = [];
      let sgpCount = 0;

      if (isSGPResponse || isCachedSGP) {
        // ⭐ Handle SGP results - filter by legs array instead of market/selection
        transformedResults = items
          .filter(o => o && (Array.isArray(o.legs) || o.type === 'sgp'))
          .slice(0, 100)
          .map((o, idx) => {
            const price = o.price ?? o.combined_price ?? o.parlay_price ?? null;
            const ev = o.ev ?? o.best_ev ?? o.ev_percent ?? null;
            const legCount = o.leg_count || (o.legs?.length || 2);
            
            // Build a description from legs
            const legDescriptions = (o.legs || []).slice(0, 3).map(leg => 
              `${leg.selection || leg.name}${leg.points ? ` ${leg.points}` : ''}`
            ).join(' + ');

            return {
              id: o.id || `sgp-${idx}`,
              type: 'sgp',
              league: (o.league || o.sport || '').toUpperCase(),
              date: o.start_date ? new Date(o.start_date).toLocaleDateString() : 'TBD',
              time: o.start_date ? new Date(o.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'TBD',
              game: `${o.away_team || ''} @ ${o.home_team || ''}` || 'N/A',
              market: `${legCount}-Leg SGP`,
              bet: legDescriptions || 'Same Game Parlay',
              book: o.sportsbook || o.best_book || 'DraftKings',
              odds: price !== null ? (price > 0 ? `+${price}` : `${price}`) : 'N/A',
              booksCalculated: o.all_books?.length || 1,
              evPercent: ev !== null ? parseFloat(ev).toFixed(2) : '0.00',
              legs: o.legs || [],
              legCount,
              theoretical: o.theoretical || false,
              _isCachedSGP: true,
              _source: o._source || 'sgp_cache',
              raw: o
            };
          })
          .sort((a, b) => parseFloat(b.evPercent) - parseFloat(a.evPercent));
        
        sgpCount = transformedResults.length;
      } else {
        // ⭐ Handle straight bet results (original logic)
        transformedResults = items
          .filter(o => o && (o.market || o.market_id) && (o.selection || o.name))
          .slice(0, 100)
          .map((o, idx) => {
            const price = o.price ?? o.american_price ?? null;
            const aiProb = o.ai_prob ?? null;
            let implied = null;
            let ev = null;

            if (typeof price === 'number' && !isNaN(price)) {
              implied = price > 0 ? (100 / (price + 100)) : ((-price) / ((-price) + 100));
            }

            if (aiProb !== null && implied !== null) {
              ev = (aiProb - implied) * 100;
            }

            const bookOdds = o.bookOdds || {};
            const bookCount = Object.keys(bookOdds).length || (o.sportsbook_count || o.book_count || 1);

            return {
              id: o.id || `${idx}`,
              league: (o.league || o.sport || '').toUpperCase(),
              date: o.commence_time ? new Date(o.commence_time).toLocaleDateString() : 'TBD',
              time: o.commence_time ? new Date(o.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'TBD',
              game: o.fixture || o.game || `${o.home_team || ''} vs ${o.away_team || ''}` || 'N/A',
              market: o.market || o.market_id || 'N/A',
              bet: `${o.selection || o.name}${o.points || o.selection_line ? ` ${o.points || o.selection_line}` : ''}`,
              book: o.sportsbook || 'N/A',
              odds: price !== null ? (price > 0 ? `+${price}` : `${price}`) : 'N/A',
              booksCalculated: bookCount,
              bookOdds,
              evPercent: ev !== null ? ev.toFixed(2) : '0.00',
              raw: o
            };
          })
          .sort((a, b) => parseFloat(b.evPercent) - parseFloat(a.evPercent));
      }

      const durationMs = Date.now() - startTime;

      // Update to completed
      updateQueryState(queryId, {
        status: QueryStatus.COMPLETED,
        progress: 100,
        progressMessage: sgpCount > 0 
          ? `Found ${sgpCount} SGPs` 
          : `Found ${transformedResults.length} results`,
        results: transformedResults,
        cached: queryResult.cached || isCachedSGP || false,
        sgpCount,
        durationMs
      });

      // Save to history
      await saveToHistory({
        id: queryId,
        prompt,
        params,
        status: QueryStatus.COMPLETED,
        results: transformedResults,
        cached: queryResult.cached || false,
        sgpCount,  // ⭐ Fixed: was hardcoded to 0
        durationMs
      });

      // Notify completion
      notifyQueryComplete(queryId, true);

    } catch (error) {
      console.error('[BackgroundQuery] Error processing query:', error);
      const durationMs = Date.now() - startTime;

      updateQueryState(queryId, {
        status: QueryStatus.ERROR,
        progress: 0,
        progressMessage: 'Query failed',
        error: error.message,
        durationMs
      });

      // Save error to history
      await saveToHistory({
        id: queryId,
        prompt,
        params,
        status: QueryStatus.ERROR,
        error: error.message,
        durationMs
      });

      notifyQueryComplete(queryId, false);
    }
  };

  // Update query state
  const updateQueryState = (queryId, updates) => {
    setActiveQueries(prev => prev.map(q => 
      q.id === queryId ? { ...q, ...updates } : q
    ));
  };

  // Notify query completion
  const notifyQueryComplete = (queryId, success) => {
    // Play notification sound (if available)
    if (Platform.OS === 'web') {
      try {
        // Browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          const query = activeQueries.find(q => q.id === queryId);
          new Notification('Query Complete', {
            body: success 
              ? `Found ${query?.results?.length || 0} results for "${query?.prompt?.slice(0, 30)}..."` 
              : 'Query failed',
            icon: '/favicon.ico'
          });
        }
        
        // Dispatch custom event for UI notification
        window.dispatchEvent(new CustomEvent('backgroundQueryComplete', {
          detail: { queryId, success }
        }));
      } catch (e) {
        console.warn('[BackgroundQuery] Notification error:', e);
      }
    }
  };

  // Cancel a query
  const cancelQuery = useCallback((queryId) => {
    setActiveQueries(prev => prev.map(q => 
      q.id === queryId && (q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING)
        ? { ...q, status: QueryStatus.CANCELLED, progressMessage: 'Cancelled' }
        : q
    ));
  }, []);

  // Remove a query from active list
  const removeQuery = useCallback((queryId) => {
    setActiveQueries(prev => prev.filter(q => q.id !== queryId));
  }, []);

  // Mark a completed query as viewed
  const markQueryViewed = useCallback((queryId) => {
    setViewedCompletedQueries(prev => new Set([...prev, queryId]));
  }, []);

  // Get unviewed completed queries count
  const getUnviewedCount = useCallback(() => {
    return activeQueries.filter(
      q => q.status === QueryStatus.COMPLETED && !viewedCompletedQueries.has(q.id)
    ).length;
  }, [activeQueries, viewedCompletedQueries]);

  // Clear all completed/cancelled queries
  const clearCompletedQueries = useCallback(() => {
    setActiveQueries(prev => prev.filter(
      q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
    ));
  }, []);

  // Delete a query from history
  const deleteFromHistory = async (historyId) => {
    const user = auth.currentUser;
    if (!user?.uid) return;

    try {
      await deleteDoc(doc(db, 'users', user.uid, 'query_history', historyId));
      setQueryHistory(prev => prev.filter(h => h.id !== historyId));
    } catch (error) {
      console.error('[BackgroundQuery] Error deleting from history:', error);
    }
  };

  // Clear all history
  const clearHistory = async () => {
    const user = auth.currentUser;
    if (!user?.uid) return;

    try {
      const q = query(collection(db, 'users', user.uid, 'query_history'));
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(d => 
        deleteDoc(doc(db, 'users', user.uid, 'query_history', d.id))
      );
      
      await Promise.all(deletePromises);
      setQueryHistory([]);
    } catch (error) {
      console.error('[BackgroundQuery] Error clearing history:', error);
    }
  };

  // Check if any queries are currently processing
  const hasProcessingQueries = activeQueries.some(
    q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
  );

  // Get the number of active (non-completed) queries
  const activeQueryCount = activeQueries.filter(
    q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
  ).length;

  // Toggle modal position between bottom-right and bottom-left
  const toggleModalPosition = useCallback(() => {
    setModalPosition(prev => prev === 'bottom-right' ? 'bottom-left' : 'bottom-right');
  }, []);

  // Register a foreground query (called from SgpFlowScreen when starting a query)
  // This allows us to track it and convert to background if user navigates away
  const registerForegroundQuery = useCallback((prompt, params, progressCallback) => {
    const queryId = generateQueryId();
    setForegroundQuery({
      id: queryId,
      prompt,
      params,
      status: QueryStatus.PROCESSING,
      progress: 0,
      progressMessage: 'Processing...',
      startTime: Date.now(),
      progressCallback
    });
    return queryId;
  }, []);

  // Clear foreground query (called when query completes in foreground)
  const clearForegroundQuery = useCallback(() => {
    setForegroundQuery(null);
  }, []);
  
  // ⭐ Complete foreground query and save to history
  const completeForegroundQuery = useCallback(async (results, options = {}) => {
    if (!foregroundQuery) {
      console.log('[BackgroundQuery] No foreground query to complete');
      return;
    }
    
    const { id, prompt, params, startTime } = foregroundQuery;
    const durationMs = Date.now() - (startTime || Date.now());
    
    // Save to history
    await saveToHistory({
      id,
      prompt,
      params,
      status: QueryStatus.COMPLETED,
      results: results || [],
      cached: options.cached || false,
      sgpCount: options.sgpCount || results?.length || 0,
      durationMs
    });
    
    console.log('[BackgroundQuery] ✅ Foreground query saved to history:', { prompt, resultsCount: results?.length || 0 });
    
    // Clear foreground query
    setForegroundQuery(null);
  }, [foregroundQuery, saveToHistory]);

  // Convert foreground query to background (called when user navigates away)
  const convertToBackground = useCallback(async () => {
    if (!foregroundQuery) {
      return { success: false, error: 'No foreground query to convert' };
    }

    const { id, prompt, params, progress, progressMessage, startTime } = foregroundQuery;
    
    // Check if this query is already in background (prevent duplicates)
    const normalizedPrompt = prompt?.trim()?.toLowerCase();
    const alreadyExists = activeQueries.some(q => 
      q.prompt?.trim()?.toLowerCase() === normalizedPrompt ||
      q.id === id
    );
    
    if (alreadyExists) {
      console.log('[BackgroundQuery] Query already exists in background, skipping conversion');
      setForegroundQuery(null);
      return { success: true, queryId: id, duplicate: true };
    }

    // Check if we've reached max concurrent queries
    const processingQueries = activeQueries.filter(
      q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
    );
    
    if (processingQueries.length >= MAX_CONCURRENT_QUERIES) {
      return { 
        success: false, 
        error: 'Maximum concurrent background queries (3) reached' 
      };
    }

    // Create background query from foreground state
    const backgroundQuery = {
      id,
      prompt,
      params,
      status: QueryStatus.PROCESSING,
      progress: progress || 10,
      progressMessage: progressMessage || 'Processing in background...',
      results: null,
      error: null,
      startTime,
      cached: false,
      sgpCount: 0,
      durationMs: 0,
      convertedFromForeground: true
    };

    // Add to active queries
    setActiveQueries(prev => [...prev, backgroundQuery]);
    
    // Clear foreground query
    setForegroundQuery(null);
    
    // Expand the modal so user can see it
    setIsMinimized(false);

    // Continue processing in background
    processQuery(id, prompt, params);

    return { success: true, queryId: id };
  }, [foregroundQuery, activeQueries]);

  // Send current query directly to background (user clicks "Send to Background" button)
  const sendToBackground = useCallback(async (prompt, params) => {
    // First clear the foreground query if it matches
    if (foregroundQuery?.prompt === prompt) {
      return convertToBackground();
    }
    
    // Otherwise start a new background query
    return addBackgroundQuery(prompt, params);
  }, [foregroundQuery, convertToBackground, addBackgroundQuery]);

  // Check if we can add more background queries
  const canAddBackgroundQuery = activeQueryCount < MAX_CONCURRENT_QUERIES;

  return (
    <BackgroundQueryContext.Provider value={{
      // State
      activeQueries,
      queryHistory,
      historyLoading,
      isMinimized,
      hasProcessingQueries,
      activeQueryCount,
      modalPosition,
      isMobileHidden,
      foregroundQuery,
      canAddBackgroundQuery,
      
      // Actions
      addBackgroundQuery,
      cancelQuery,
      removeQuery,
      markQueryViewed,
      getUnviewedCount,
      clearCompletedQueries,
      setIsMinimized,
      toggleModalPosition,
      setIsMobileHidden,
      
      // Foreground-to-background conversion
      registerForegroundQuery,
      clearForegroundQuery,
      completeForegroundQuery,
      convertToBackground,
      sendToBackground,
      
      // History actions
      loadQueryHistory,
      deleteFromHistory,
      clearHistory,
      
      // Constants
      MAX_CONCURRENT_QUERIES,
      QueryStatus,
      IS_MOBILE
    }}>
      {children}
    </BackgroundQueryContext.Provider>
  );
}

export function useBackgroundQuery() {
  const context = useContext(BackgroundQueryContext);
  if (context === undefined) {
    throw new Error('useBackgroundQuery must be used within a BackgroundQueryProvider');
  }
  return context;
}

